/**
 * Tailwind classes that add a fixed offset on top of the device safe area.
 *
 * Read `--safe-area-inset-*` first and only fall back to `env(safe-area-inset-*)`.
 * On Android, Capacitor's SystemBars plugin (`insetsHandling: "css"`, see
 * `capacitor.config.ts`) pads the WebView's parent view natively and rewrites the
 * insets handed to the WebView to zero, publishing the real values as inline
 * custom properties on `<html>` instead. So `env()` alone resolves to `0px` there
 * and the offset is all that survives. The `env()` fallback keeps web/PWA working,
 * where Capacitor injects nothing.
 *
 * Every class below has to stay a complete literal string in this file. Tailwind
 * finds utilities by scanning source text, so a generated
 * `` `pt-[calc(...)+${offset}]` `` emits no CSS at all — it fails silently, with
 * the padding simply missing. Adding an offset means adding a map entry here.
 */

const TOP_PADDING = {
  "16px": "pt-[calc(var(--safe-area-inset-top,env(safe-area-inset-top,0px))+16px)]",
  "84px": "pt-[calc(var(--safe-area-inset-top,env(safe-area-inset-top,0px))+84px)]",
} as const;

const BOTTOM_PADDING = {
  "12px": "pb-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+12px)]",
  "80px": "pb-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+80px)]",
} as const;

/**
 * Bottom padding that *is* the safe area, floored at the given value, rather than
 * adding an offset on top of it. For frames drawn with the home-indicator space
 * reserved exactly (Figma's 34px), where the floor only has to cover devices that
 * report no inset at all.
 */
const BOTTOM_PADDING_FLOOR = {
  "34px": "pb-[max(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px)),34px)]",
} as const;

/** For elements positioned off the bottom edge rather than padded. */
const BOTTOM_INSET = {
  "12px": "bottom-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+12px)]",
  "1.5rem": "bottom-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+1.5rem)]",
  "1.75rem": "bottom-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+1.75rem)]",
} as const;

export type SafeAreaTopOffset = keyof typeof TOP_PADDING;
export type SafeAreaBottomOffset = keyof typeof BOTTOM_PADDING;
export type SafeAreaBottomInsetOffset = keyof typeof BOTTOM_INSET;
export type SafeAreaBottomFloor = keyof typeof BOTTOM_PADDING_FLOOR;

interface SafeAreaPaddingOptions {
  top?: SafeAreaTopOffset;
  bottom?: SafeAreaBottomOffset;
}

/**
 * Vertical padding that clears the safe area, plus the given offset on each side.
 * Omit a side to leave it untouched.
 *
 * Pass the result through `cn` after the base classes so `twMerge` lets it win
 * over any earlier `pt-`/`pb-`.
 */
export const safeAreaPadding = ({ top, bottom }: SafeAreaPaddingOptions): string =>
  [top && TOP_PADDING[top], bottom && BOTTOM_PADDING[bottom]].filter(Boolean).join(" ");

/**
 * Bottom padding equal to the safe area itself, never less than `floor`.
 *
 * Pass the result through `cn` after the base classes so `twMerge` lets it win
 * over any earlier `pb-`.
 */
export const safeAreaPaddingFloor = (floor: SafeAreaBottomFloor): string =>
  BOTTOM_PADDING_FLOOR[floor];

/** `bottom` position that clears the safe area, plus the given offset. */
export const safeAreaBottomInset = (offset: SafeAreaBottomInsetOffset): string =>
  BOTTOM_INSET[offset];
